/**
 * @module support/customer-login
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// Core Libraries
/* tslint:disable:no-any */ // Need to support all data types
import { Component, OnDestroy, OnInit, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from '@root/app.config';
// services
import { CustomerProfileService } from '@support/customer-login/services/customer-profile.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CryptoEncrytionService } from '@support/shared/services/crypto-encryption.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { NavbarService } from '@root/shared/services/navbar.service';
import { StateStoreService } from '@support/state-object-hash-utilities/services/state-store.service';
import { SETTINGS } from '@environment/settings';
import { AuthService } from '@support/shared/services/auth.service';
// Interfaces
import {
  AnonymousTokenResponse,
  IamResponse,
} from '@support/api/proxies/responses/api-999.interface';
import { PinVerificationResponse } from '@support/api/proxies/responses/api-2235.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import {
  API331GetRolesPermission,
  Role,
} from '@experience/repdash/interfaces/API-331-get-role-permissions.interface';
// Constants
import { CUSTOMER_LOGIN_CONSTANTS } from '@support/customer-login/customer-login.constants';
import { takeUntil } from 'rxjs/operators';
import { HttpClient, HttpResponse } from '@angular/common/http';

@Component({
  selector: 'tmo-web-login',
  templateUrl: './customer-profile.component.html',
  styleUrls: ['../../../../style-sheets/components/html-spinner.component.scss'],
})
export class CustomerProfileComponent implements OnInit, OnDestroy {
  /**
   * Stores boolean value of loading status
   */
  public loadingStatus: boolean;
  /**
   * Stores value of customer's email address
   */
  public customEmail: string;
  /**
   * Stores value of customer's phone number
   */
  public customMsisdn: string;
  /**
   * Stores value of current host name
   */
  public redirectUri: string;
  /**
   * Stores value of API 999 response
   */
  public profile: IamResponse;
  /**
   * Stores roles permissions coming from API 331
   */
  public lineRoles: API331GetRolesPermission;
  /**
   * Stores selected role
   */
  public selectedRole: Role;
  /**
   * Stores boolean value for WaitForRoles
   */
  public waitForRoles: boolean;
  /**
   * Stores flag value for multiple account
   */
  public mutipleAccount: boolean;
  /**
   * Stores the value of DynamicModal
   */
  public validatePinModal: DynamicModal;
  /**
   * Stores the value of StaticModal
   */
  public verifyPinErrorModal: StaticModal;
  /**
   * Stores the value of Merging status
   */
  public profileMergingStatus: boolean;
  /**
   * Stores the value to show error message for pin validation
   */
  public invalidPinError: boolean;

  /**
   * ngUnsubscribe is used to destroy subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private commonUtils: CommonUtilService,
    private router: Router,
    private customerProfileService: CustomerProfileService,
    private cryptoEncryptionService: CryptoEncrytionService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private navbarService: NavbarService,
    private appConfig: AppConfig,
    private objectService: ObjectService,
    private stateStoreService: StateStoreService,
    private authService: AuthService
  ) {
    this.profileMergingStatus = false;
    this.loadingStatus = true;
    this.waitForRoles = false;
    this.mutipleAccount = false;
    this.ngUnsubscribe = new Subject<void>();
    this.validatePinModal = {
      disableDismiss: true,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
      modalName: 'validate-ownership-modal',
    };
    this.verifyPinErrorModal = {
      disableDismiss: true,
      disableEsc: true,
      modalName: 'verify-pin-error-modal',
      buttons: [
        {
          id: 'logOut',
          type: 'cancel',
          name: 'Logout',
          class: 'btn btn-secondary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
  }

  public ngOnInit(): void {
    this.customerProfileService.validateOwnershipModalSubject.subscribe((modal: string) => {
      if (modal === 'pin') {
        this.validatePinModal.toggleModalEmitter.emit({ open: true, close: false });
      } else {
        this.verifyPinErrorModal.headerText = 'Error!';
        this.verifyPinErrorModal.bodyText =
          'You are part of a multi-line account and the primary line is already registered to a T-Mobile ID. Please see your account owner for assistance with your online account needs.';
        this.verifyPinErrorModal.toggleModalEmitter.emit({ open: true });
      }
    });
    if (this.commonUtils.isNotEmpty(this.route.snapshot.paramMap.get('lang'))) {
      this.setRedirectUri(this.route.snapshot.params.lang);
    } else {
      this.setRedirectUri();
    }
    if (this.commonUtils.isNotEmpty(this.route.snapshot.paramMap.get('path'))) {
      this.gotoEui(this.route.snapshot.params.path);
    } else if (this.commonUtils.isNotEmpty(this.stateStoreUtilsService.getAuthCode())) {
      this.loadingStatus = this.appConfig.getConfig('enableBridging');
    } else {
      this.router.navigate([CUSTOMER_LOGIN_CONSTANTS.HOME_PATH]);
    }
  }

  /**
   * Method to call API 999 with provided inputs from MSISDN-Email form
   * @param email
   * @param msisdn
   * @return void
   */
  public authorizeInputs(userId: string, msisdn: string, code?: string): void {
    this.stateStoreUtilsService.setRedeemCardMsisdn(msisdn);
    const formatMsisdn = this.commonUtils.formatMsisdnTenDigits(msisdn);
    this.waitForRoles = true;
    const authCode = code ? code : this.stateStoreUtilsService.getAuthCode();
    this.customerProfileService
      .getAuthToken(
        authCode,
        formatMsisdn,
        this.redirectUri,
        this.stateStoreUtilsService.getEservicesParams().userId
      )
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (data: IamResponse) => {
          this.stateStoreUtilsService.setCorrelation(data.correlation);
          // to persist interactionId
          this.stateStoreUtilsService.overrideInteractionIdForLogin(data.correlation , data.token);
          this.authService.setCookie('dnt', data.localDoNotSellSetting);
          if (data.profile.hasOwnProperty('accountOwnerExists')) {
            if (!data.profile.accountOwnerExists) {
              this.stateStoreUtilsService.setToken(data.iamToken);
              this.stateStoreUtilsService.setEservicesParams({
                msisdn: msisdn,
                userId: userId,
                code: authCode,
              });
              this.stateStoreUtilsService.setJWTToken(data.token);
              this.validatePinModal.toggleModalEmitter.emit({ open: true });
            } else {
              this.verifyPinErrorModal.bodyText =
                'You are part of a multi-line account and the primary line is already registered to a T-Mobile ID. Please see your account owner for assistance with your online account needs.';
              this.verifyPinErrorModal.toggleModalEmitter.emit({ open: true });
            }
          } else {
            clearInterval(this.customerProfileService.interval as NodeJS.Timeout);
            this.profile = data;
            this.setProfile(this.profile);
            this.stateStoreUtilsService.clearAuthCode();
            this.stateStoreUtilsService.setCorrelation(data.correlation);
            if (data && data.profile) {
              this.stateStoreUtilsService.setBscsCustomerID(data.profile.id);
              this.stateStoreUtilsService.setRepActiveAccountId(data.profile.accountId);
            }
            this.customerProfileService
              .getPermissions(
                this.stateStoreUtilsService.getCustomerId(),
                this.stateStoreUtilsService.getAccountId()
              )
              .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
              .subscribe(
                (permissions: HttpResponse<Object>) => {
                  this.stateStoreService.setState('app.permissionObject', permissions.body);
                  this.customerProfileService.permissionsLoadedSource.next(true);
                  this.stateStoreUtilsService.setZipCodeModalFlag(false);
                },
                () => {
                  this.waitForRoles = false;
                }
              );
          }
          if (this.customerProfileService.callSetDNS(data, this.authService.getDNS())) {
            this.customerProfileService
              .setDNS(data)
              .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
              .subscribe();
          }
        },
        () => {
          this.waitForRoles = false;
          this.router.navigate(['/error']);
        }
      );
  }

  /**
   * Method to redirect to EUI login page
   * @param authRoot
   * @return void
   */
  public gotoEui(authRoot: string): void {
    const win = window;
    if (authRoot && authRoot !== 'auth') {
      const eServiceRoot = this.appConfig
        .getConfig('eServiceRoot')
        .concat('&state=', encodeURIComponent(authRoot));
      win.location.href = eServiceRoot;
    } else {
      const eService = this.appConfig.getConfig('eServiceRoot');
      win.location.href = eService;
    }
  }

  /**
   * Method to validate customer's pin and merge profiles
   * @param pin
   * @return void
   */
  public validatePinAndMerge(pin: string): void {
    this.profileMergingStatus = true;
    this.invalidPinError = false;
    this.customerProfileService
      .validatePin(this.cryptoEncryptionService.encryptWithAES(pin))
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (data: PinVerificationResponse) => {
          this.profileMergingStatus = false;
          if (data.isPinValid) {
            this.validatePinModal.toggleModalEmitter.emit({ open: false, close: true });
            if (data.isMergeSuccess) {
              this.customerProfileService
                .getAnonymousToken()
                .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
                .subscribe((anonResponse: AnonymousTokenResponse) => {
                  const accessToken = this.objectService.get(anonResponse, 'access_token');
                  const correlation = this.objectService.get(anonResponse, 'correlation');
                  this.stateStoreUtilsService.setCorrelation(correlation);
                  this.stateStoreUtilsService.setToken(accessToken);
                  const eservicesParams = this.stateStoreUtilsService.getEservicesParams();
                  this.authorizeInputs(
                    eservicesParams.userId,
                    eservicesParams.msisdn,
                    eservicesParams.code
                  );
                });
            } else {
              this.showMergeProfileFailureModal();
            }
          } else {
            const MAX_ATTEMPTS = 5;
            const FINAL_ATTEMPT = 4;
            if (data.noOfUserAttempts === MAX_ATTEMPTS) {
              this.verifyPinErrorModal.headerText = 'Error!';
              this.verifyPinErrorModal.bodyText =
                'We are unable to verify the PIN/Passcode at this time. Please try again later.';
              this.validatePinModal.toggleModalEmitter.emit({
                open: false,
                close: true,
              });
              this.verifyPinErrorModal.toggleModalEmitter.emit({
                open: true,
              });
            } else if (data.noOfUserAttempts === FINAL_ATTEMPT) {
              this.verifyPinErrorModal.headerText = 'Oops!';
              this.verifyPinErrorModal.bodyText =
                'We are unable to verify the PIN you entered. You have 1 remaining attempt.';
              this.verifyPinErrorModal.buttons.push({
                id: 'closeModal',
                type: 'submit',
                name: 'Okay',
                class: 'btn btn-primary',
              });
              this.invalidPinError = true;
              this.verifyPinErrorModal.toggleModalEmitter.emit({
                open: true,
              });
            } else {
              this.invalidPinError = true;
            }
          }
        },
        () => {
          this.profileMergingStatus = false;
          this.showMergeProfileFailureModal();
        }
      );
  }

  /**
   * Method to handle the selection from pin error modal
   * @return void
   */
  public handlePinErrorModal(event: Event): void {
    const key = 'id';
    const id = event[key];
    if (id === 'logOut') {
      this.signoutUnverifiedCustomer();
    } else if (id === 'closeModal') {
      this.verifyPinErrorModal.buttons = [
        {
          id: 'logOut',
          type: 'cancel',
          name: 'Logout',
          class: 'btn btn-secondary',
        },
      ];
      this.verifyPinErrorModal.toggleModalEmitter.emit({ open: false, close: true });
    }
  }

  /**
   * Method to signout customer when authorization fails
   * @return void
   */
  public signoutUnverifiedCustomer(): void {
    this.navbarService.setIsLoggedIn(true);
    const win = window;
    this.stateStoreService.clearState();
    win.open(
      `${SETTINGS.EUIROOT}signout?redirect_uri=${win.location.protocol}//${win.location.host}`,
      '_self'
    );
  }

  /**
   * Method to signout customer when authorization fails
   * @return void
   */
  public showMergeProfileFailureModal(): void {
    this.verifyPinErrorModal.headerText = 'Error!';
    this.verifyPinErrorModal.bodyText =
      'We have run into an issue when linking this phone number to the T-Mobile ID. Please try again later.';
    this.verifyPinErrorModal.toggleModalEmitter.emit({ open: true });
  }

  /**
   * Method to set the customer's profile information on state store for reusability
   * @param loginResponse
   * @return void
   */
  public setProfile(loginResponse: IamResponse): void {
    this.stateStoreUtilsService.setWebAuthResponse(loginResponse);
    this.stateStoreUtilsService.setMsisdn(loginResponse.profile.msisdn);
    this.customerProfileService.setMutipleAccountValue(loginResponse.profile.multipleAccounts);
    this.verifyCustomer(loginResponse.profile.id, loginResponse.profile.accountId);
  }

  /**
   * Function called on click of Authorize button.
   * @param custId
   * @param accountId
   * @return void
   */
  public verifyCustomer(custId: String, accountId: String): void {
    this.navbarService.setIsLoggedIn(true);
    this.navbarService.navbarChangeEmitter.emit({ id: 'loggedIn' });
    this.customerProfileService.permissionsLoaded$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: boolean) => {
        const eServicesState = this.stateStoreUtilsService.getEservicesState();
        const whitelistedDeeplinkUrls = this.appConfig.getConfig('whitelistedDeepLinkUrls') || [];
        if (!!eServicesState && whitelistedDeeplinkUrls.includes(eServicesState)) {
          this.router.navigate([eServicesState]);
          this.stateStoreUtilsService.setEservicesState('');
        } else {
          this.router.navigate([CUSTOMER_LOGIN_CONSTANTS.ACCOUNT_LANDING_PATH]);
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setRedirectUri(lang?: string): void {
    const win = window;
    let langUriConfig: string;
    if (lang) {
      langUriConfig = `${lang}.`;
      this.redirectUri = `${win.location.protocol}//${langUriConfig}${win.location.host}`;
    } else {
      this.redirectUri = `${win.location.protocol}//${win.location.host}`;
    }
  }
}
